import React, { useCallback, useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Save, Sliders, ShieldAlert } from "lucide-react";

/**
 * Founder-only Institutional Settings page.
 *
 * Sprint 1 (Grant Discovery) scope: settings that govern discovery ranking and
 * — later — Sprint 2 LLM auto-analyze thresholds.
 */
export default function InstitutionalSettings() {
  const { user } = useAuth();
  const isFounder = (user?.role === "founder") || (user?.role === "admin");
  const [settings, setSettings] = useState(null);
  const [saving, setSaving] = useState(false);
  const [csvGeo, setCsvGeo] = useState("");
  const [csvPri, setCsvPri] = useState("");
  const [csvInc, setCsvInc] = useState("");
  const [csvExc, setCsvExc] = useState("");
  const [csvAffinity, setCsvAffinity] = useState("");

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/institutional-settings");
      setSettings(data);
      setCsvGeo((data.geographic_focus || []).join(", "));
      setCsvPri((data.funding_priorities || []).join(", "));
      setCsvInc((data.categories_include || []).join(", "));
      setCsvExc((data.categories_exclude || []).join(", "));
      setCsvAffinity((data.affinity_funders || []).join(", "));
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    }
  }, []);

  useEffect(() => { if (isFounder) load(); }, [isFounder, load]);

  const parseCsv = (s) => s.split(",").map(x => x.trim()).filter(Boolean);

  const save = async (patch) => {
    setSaving(true);
    try {
      const { data } = await api.patch("/institutional-settings", patch);
      setSettings(data);
      toast.success("Settings saved");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    } finally { setSaving(false); }
  };

  if (!isFounder) {
    return (
      <div className="max-w-lg mx-auto p-8 border border-line rounded-md bg-bone">
        <div className="flex items-center gap-3 text-terracotta">
          <ShieldAlert className="h-5 w-5" />
          <div className="font-serif text-xl">Founder access required</div>
        </div>
      </div>
    );
  }

  if (!settings) return <div className="p-8 text-ink-soft">Loading settings…</div>;

  return (
    <div className="space-y-8 max-w-4xl" data-testid="institutional-settings-page">
      <div>
        <div className="flex items-baseline gap-3">
          <h1 className="font-serif text-3xl text-ink flex items-center gap-2">
            <Sliders className="h-6 w-6" strokeWidth={1.5} />
            Institutional Settings
          </h1>
          <Badge variant="outline" className="text-[10px] uppercase tracking-[0.18em]">Founder only</Badge>
        </div>
        <p className="text-sm text-ink-soft mt-2 max-w-2xl">
          Every AI Staff member reads these. Changes take effect on the next Discovery poll or &ldquo;Recompute scores&rdquo; click.
          <strong className="text-ink"> Zero recurring LLM cost.</strong>
        </p>
      </div>

      {/* Fit-score threshold */}
      <section className="border border-line rounded-md bg-bone p-5">
        <div className="font-serif text-lg text-ink mb-1">Analysis Threshold (Sprint 2 cost control)</div>
        <p className="text-xs text-ink-soft mb-3">
          Minimum composite fit score required before an opportunity is eligible for LLM auto-analysis.
          Higher = fewer analyses = lower recurring credit cost. This has <em>no effect on Sprint 1</em> — it prepares Sprint 2.
        </p>
        <div className="flex items-center gap-3">
          <Input type="number" min={0} max={100} defaultValue={settings.min_fit_score_for_analysis}
            className="w-32"
            onBlur={(e) => {
              const v = parseInt(e.target.value);
              if (!isNaN(v) && v !== settings.min_fit_score_for_analysis) {
                save({ min_fit_score_for_analysis: v });
              }
            }}
            data-testid="settings-fit-threshold" />
          <span className="text-xs text-ink-soft">0 – 100. Current: {settings.min_fit_score_for_analysis}</span>
        </div>
      </section>

      {/* Dashboard filter */}
      <section className="border border-line rounded-md bg-bone p-5">
        <div className="font-serif text-lg text-ink mb-1">Dashboard Display Threshold</div>
        <p className="text-xs text-ink-soft mb-3">Opportunities scoring below this level are hidden from the Discovery dashboard by default.</p>
        <Input type="number" min={0} max={100} defaultValue={settings.min_fit_score_for_display}
          className="w-32"
          onBlur={(e) => {
            const v = parseInt(e.target.value);
            if (!isNaN(v) && v !== settings.min_fit_score_for_display) save({ min_fit_score_for_display: v });
          }}
          data-testid="settings-display-threshold" />
      </section>

      {/* CSV lists */}
      <section className="border border-line rounded-md bg-bone p-5 space-y-4">
        <div className="font-serif text-lg text-ink">Filters (comma-separated)</div>
        {[
          ["Geographic focus", csvGeo, setCsvGeo, "geographic_focus"],
          ["Funding priorities", csvPri, setCsvPri, "funding_priorities"],
          ["Categories include", csvInc, setCsvInc, "categories_include"],
          ["Categories exclude", csvExc, setCsvExc, "categories_exclude"],
          ["Affinity funders (boost prior/preferred funders)", csvAffinity, setCsvAffinity, "affinity_funders"],
        ].map(([label, value, setter, key]) => (
          <div key={key}>
            <label className="text-xs text-ink-soft block mb-1">{label}</label>
            <div className="flex gap-2">
              <Input value={value} onChange={e => setter(e.target.value)}
                data-testid={`settings-${key}`}
                className="flex-1" />
              <Button
                size="sm" variant="outline"
                onClick={() => save({ [key]: parseCsv(value) })}
                disabled={saving}
              >
                <Save className="h-3.5 w-3.5 mr-1" strokeWidth={1.5} /> Save
              </Button>
            </div>
          </div>
        ))}
      </section>

      {/* Weights */}
      <section className="border border-line rounded-md bg-bone p-5">
        <div className="font-serif text-lg text-ink mb-1">Composite Score Weights</div>
        <p className="text-xs text-ink-soft mb-3">
          Weights must sum to ~1.0. Advanced — edit only if the default ranking doesn&rsquo;t match your priorities.
        </p>
        <div className="grid gap-2">
          {Object.entries(settings.score_weights || {}).map(([k, v]) => (
            <div key={k} className="grid grid-cols-[1fr,120px] items-center gap-3">
              <span className="text-sm text-ink capitalize">{k.replace(/_/g, " ")}</span>
              <Input type="number" min={0} max={1} step={0.05} defaultValue={v}
                onBlur={(e) => {
                  const val = parseFloat(e.target.value);
                  if (!isNaN(val)) {
                    save({ score_weights: { ...settings.score_weights, [k]: val } });
                  }
                }}
                data-testid={`settings-weight-${k}`}
              />
            </div>
          ))}
          <div className="text-xs text-ink-soft mt-2">
            Current sum: <strong>{Object.values(settings.score_weights || {}).reduce((a, b) => a + b, 0).toFixed(2)}</strong>
          </div>
        </div>
      </section>

      <div className="text-xs text-ink-soft italic">
        Last updated: {settings.updated_at ? new Date(settings.updated_at).toLocaleString() : "never"}
        {settings.updated_by && ` by ${settings.updated_by}`}
      </div>
    </div>
  );
}
